import { useEffect, useRef } from "react";
import type { ContributionKitCopy } from "./contribution-kit-copy";

/**
 * Keyboard shortcuts of a task page.
 *
 * ```ts
 * useTaskShortcuts([
 *   { key: " ", run: toggleRecording, enabled: canRecord },
 *   { key: "Enter", run: sendAndNext },
 *   { key: "ArrowLeft", run: previous },
 * ]);
 * ```
 *
 * `key` is a KeyboardEvent.key, compared case-insensitively for letters, or
 * MOD_ENTER for the send action of pages where you type (Ctrl/Cmd+Enter works
 * from inside the field, plain Enter outside it).
 * Shortcuts never fire while the person types in a field, with a modifier
 * held, on a held (repeating) key, or when a dialog or menu is open. Arrows
 * inside a radio group or tab list move between its items. Space and Enter on
 * a button reached with Tab keep their native click; on a button just clicked
 * with the mouse the shortcut wins, so Space plays the audio instead of
 * re-clicking "Femme".
 */
export interface TaskShortcut {
  key: string;
  run: () => void;
  enabled?: boolean;
}

const EDITABLE = "input, textarea, select, [contenteditable=''], [contenteditable='true']";
const ACTIVATABLE = "button, a[href], [role='button'], [role='radio'], [role='checkbox'], [role='switch'], [role='option'], [role='menuitem'], [role='combobox']";
// Open popups own every key (arrows, Enter, type-ahead letters).
const POPUP = "[role='listbox'], [role='menu'], [role='menubar'], [data-radix-popper-content-wrapper]";
// Widgets that move between their items with the arrow keys.
const ARROW_WIDGET = "[role='radiogroup'], [role='tablist'], [role='slider'], [role='grid'], [role='tree']";
const ARROWS = new Set(["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"]);

/** Ctrl+Enter (Cmd+Enter on Mac): sends from inside the text field too. */
export const MOD_ENTER = "Mod+Enter";

type ShortcutEvent = Pick<KeyboardEvent, "key" | "altKey" | "ctrlKey" | "metaKey" | "target" | "defaultPrevented"> & { repeat?: boolean };

function popupOpen(): boolean {
  return typeof document !== "undefined" && document.querySelector("[data-radix-popper-content-wrapper], [role='menu'][data-state='open'], [role='listbox'][data-state='open']") !== null;
}

function dialogOpen(): boolean {
  return typeof document !== "undefined" && document.querySelector("[role='dialog'][data-state='open'], [role='alertdialog'][data-state='open']") !== null;
}

/**
 * `clicked` is the control last pressed with the mouse or a finger (null
 * after a Tab): it keeps the focus, but Space and Enter then belong to the
 * page shortcuts. Chrome's :focus-visible can't tell, it turns on at the
 * first key press.
 */
export function shortcutFor(event: ShortcutEvent, shortcuts: TaskShortcut[], clicked: Element | null = null): TaskShortcut | null {
  // A held key repeats: it must not start and stop a recording in a loop.
  if (event.defaultPrevented || event.altKey || event.repeat) return null;
  const target = event.target instanceof Element ? event.target : null;
  // A modal dialog or an open menu owns the keyboard.
  if (target?.closest("[role='dialog'], [role='alertdialog']") || dialogOpen()) return null;
  if (target?.closest(POPUP) || popupOpen()) return null;

  const enabled = shortcuts.filter((shortcut) => shortcut.enabled !== false);
  if (event.ctrlKey || event.metaKey) {
    return event.key === "Enter" ? enabled.find((shortcut) => shortcut.key === MOD_ENTER) ?? null : null;
  }
  if (target?.closest(EDITABLE)) return null;
  if (ARROWS.has(event.key) && target?.closest(ARROW_WIDGET)) return null;
  // Space and Enter on a button reached with Tab keep their native click.
  const control = target?.closest(ACTIVATABLE) ?? null;
  if ((event.key === " " || event.key === "Enter") && control && control !== clicked) return null;

  const pressed = event.key.length === 1 ? event.key.toLowerCase() : event.key;
  return (
    enabled.find((shortcut) => {
      // Outside a field, plain Enter also runs the Ctrl+Enter action.
      const key = shortcut.key === MOD_ENTER ? "Enter" : shortcut.key.length === 1 ? shortcut.key.toLowerCase() : shortcut.key;
      return key === pressed;
    }) ?? null
  );
}

export function useTaskShortcuts(shortcuts: TaskShortcut[], active = true) {
  // The latest list, without re-binding the listener on every render.
  const ref = useRef(shortcuts);
  ref.current = shortcuts;

  useEffect(() => {
    if (!active) return;
    let clicked: Element | null = null;
    const onPointerDown = (event: PointerEvent) => {
      clicked = event.target instanceof Element ? event.target.closest(ACTIVATABLE) : null;
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Tab") {
        clicked = null;
        return;
      }
      const match = shortcutFor(event, ref.current, clicked);
      if (!match) return;
      event.preventDefault();
      match.run();
    };
    window.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [active]);
}

/** How a shortcut key is printed in a key hint. */
export function shortcutLabel(key: string, copy: Pick<ContributionKitCopy, "keySpace" | "keyEnter">): string {
  if (key === " ") return copy.keySpace;
  if (key === "Enter") return copy.keyEnter;
  if (key === MOD_ENTER) return `${isMac() ? "⌘" : "Ctrl"} ${copy.keyEnter}`;
  if (key === "ArrowLeft") return "←";
  if (key === "ArrowRight") return "→";
  return key.length === 1 ? key.toUpperCase() : key;
}

function isMac(): boolean {
  return typeof navigator !== "undefined" && /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent);
}
